/**
 * Fetch Folder Contents Handler - 获取收藏夹内容列表处理器
 *
 * 负责处理 fetchFolderContents API 的完整逻辑
 */

import log from "logbeacon";
import type {
  FavoritesCenterState,
  FolderContentsOptions,
  ListPositionAssetRequest,
  OperationResult,
} from "../types";

import { OptimisticUpdateManager } from "../managers/OptimisticUpdateManager";
import type { PositionsAccountService } from "../PositionsAccountService";
import { UserStateManager } from "../managers/UserStateManager";
import { updateVersionedData } from "../managers/VersionedDataManager";
import type {
  PositionAsset,
  PositionAssetPagination,
} from "@/src/features/api/memberApi";

export class FetchFolderContentsHandler {
  constructor(
    private userStateManager: UserStateManager,
    private optimisticUpdateManager: OptimisticUpdateManager,
    private state: FavoritesCenterState,
    private positionsAccountService: PositionsAccountService | null,
    private filterNormalStatusPortfolios: (portfolioIds: string[]) => string[],
    private handleUserSwitch: () => Promise<void>,
    private getCurrentUserId: () => string | null,
    private handleError: (error: Error) => void,
  ) {}

  /**
   * Execute fetch folder contents
   */
  async execute(
    folderId: string,
    options?: FolderContentsOptions & ListPositionAssetRequest,
  ): Promise<
    OperationResult<PositionAssetPagination["rows"]> & { next_token?: string }
  > {
    try {
      // Clear previous error state
      this.state.error = null;
      this.state.isLoading = true;
      // 1. Check user switch
      await this.handleUserSwitch();

      const currentUserId = this.getCurrentUserId();

      const accountId = folderId || this.state?.userFolders?.[0]?.id;
      if (folderId === "all" || !accountId) {
        throw "folderId is illegal.";
      }
      const nextToken = decodeURIComponent(options?.next_token || "");
      const result = await this.positionsAccountService?.getUserPositions(
        accountId,
        {
          limit: options?.limit || 20,
          ...options,
          next_token: nextToken,
        },
      );

      if (result?.success && result?.data) {
        // 3. Transform data format: extract rows array from PositionAssetPagination
        const positionAssets = result?.data?.rows || [];
        const assets: PositionAsset[] = positionAssets.map(
          (item: PositionAsset) => ({
            ...item,
            // v2.0: Do not keep portfolios field (Option A), set as empty array for type compatibility
            portfolios: [],
            status: undefined,
          }),
        );

        // 4. Update pagination data (reset to first page)
        const userData = this.userStateManager.getOrCreateUserData(
          accountId || "",
        );
        if (!userData.folderPaginationData.has(accountId)) {
          userData.folderPaginationData.set(accountId, {});
        }
        const paginationData = userData.folderPaginationData.get(accountId)!;
        paginationData["page_1"] = assets;

        userData.lastAccess = Date.now();

        // 5. Update favorite states and allFavoriteData using account_id (v2.0)
        const contentIds = assets
          .map((asset) => asset.asset_id)
          .filter((id): id is string => Boolean(id));

        if (contentIds.length > 0) {
          // v2.0: Extract favorites status from PositionAsset
          // Business scenario: one asset_id corresponds to one account_id (equals folderId)
          // Note: In v2.0, portfolios field doesn't exist, changed to use account_id
          const favoriteStatesUpdates = positionAssets
            .map((item: PositionAsset) => {
              const contentId = item.asset_id;
              if (!contentId) {
                return null;
              }

              // v2.0: Use account_id as folder ID (keep array format for compatibility)
              // Prefer item.account_id, if not exists use folderId (because getUserPositions returns assets belonging to this account)
              const accountIdValue = item.account_id || accountId;
              const favoriteStates = accountIdValue ? [accountIdValue] : [];

              // Filter to keep only status = "NORMAL" folders
              // Note: In v2.0, account_id corresponding folder status needs to be obtained from userFolders
              const filteredFavoriteStates =
                this.filterNormalStatusPortfolios(favoriteStates);

              return {
                key: contentId,
                value: filteredFavoriteStates,
              };
            })
            .filter(Boolean) as Array<{ key: string; value: string[] }>;

          // Update favoriteStates cache
          const newFavoriteStates = { ...this.state.favoriteStates.data };
          favoriteStatesUpdates.forEach(({ key, value }) => {
            newFavoriteStates[key] = value;
          });
          updateVersionedData(
            this.state.favoriteStates,
            newFavoriteStates,
            true,
          );

          // Update source data cache
          if (currentUserId) {
            const userData =
              this.userStateManager.getOrCreateUserData(currentUserId);
            favoriteStatesUpdates.forEach(({ key, value }) => {
              userData.favoriteStates.set(key, value);
            });
            userData.lastAccess = Date.now();
          }

          // Update allFavoriteData summary data
          this.optimisticUpdateManager.updateAllFavoriteData(
            favoriteStatesUpdates,
          );

          log.debug(
            `[FetchFolderContentsHandler] Updated ${favoriteStatesUpdates.length} favorite states from account_id data (v2.0)`,
          );
        }

        // Clear loading state
        this.state.isLoading = false;

        log.info(
          `[FetchFolderContentsHandler] Successfully got folder contents: ${folderId}, ${assets.length} assets`,
        );

        return {
          success: true,
          data: assets,
          next_token: result.data.next_token,
        };
      } else {
        const error = result?.message || "Failed to get folder contents";
        this.state.error = error;
        this.state.isLoading = false;
        return {
          success: false,
          error,
        };
      }
    } catch (error) {
      log.error(
        "[FetchFolderContentsHandler] Failed to get folder contents:",
        error,
      );
      const errorMessage = "Failed to get data, please try again";
      this.state.error = errorMessage;
      this.state.isLoading = false;
      this.handleError(error as Error);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}
